import { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  Image, 
  Upload, 
  Trash2, 
  Loader2, 
  Lock, 
  Sparkles,
  ImagePlus,
  Crown,
  ArrowUpRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { API, API_BASE_URL } from '../config/api';
import { safeJson } from '../utils/safeJson';



export function BannerUpload({ currentBannerUrl, artistName, onUploadSuccess, getAuthHeaders }) {
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null); // 'banner' or 'background'
  const [bannerPreview, setBannerPreview] = useState(null);
  const [backgroundPreview, setBackgroundPreview] = useState(null);
  // Store the actual file objects to prevent mobile browsers from clearing them
  const [bannerFile, setBannerFile] = useState(null);
  const [backgroundFile, setBackgroundFile] = useState(null);
  const bannerInputRef = useRef(null);
  const backgroundInputRef = useRef(null);

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      const response = await fetch(`${API}/upload/image-permissions`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPermissions(data);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateFile = (file, type) => {
    const validTypes = ['image/jpeg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG or PNG only.');
      return false;
    }

    const maxSize = type === 'banner' 
      ? (permissions?.banner_requirements?.max_size_mb || 2) * 1024 * 1024
      : (permissions?.background_requirements?.max_size_mb || 3) * 1024 * 1024;

    if (file.size > maxSize) {
      toast.error(`File too large. Maximum size is ${maxSize / (1024 * 1024)}MB.`);
      return false;
    }

    return true;
  };

  const handleFileSelect = (file, type) => {
    if (!validateFile(file, type)) return;

    // Store the file in state to prevent mobile browsers from clearing it
    if (type === 'banner') {
      setBannerFile(file);
    } else {
      setBackgroundFile(file);
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      if (type === 'banner') {
        setBannerPreview(e.target.result);
      } else {
        setBackgroundPreview(e.target.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async (type) => {
    const preview = type === 'banner' ? bannerPreview : backgroundPreview;
    const file = type === 'banner' ? bannerFile : backgroundFile;
    
    // Use stored file from state instead of file input (more reliable on mobile)
    if (!preview || !file) {
      toast.error('Please select an image first');
      return;
    }

    // Debug logging for production issues
    console.log(`[BannerUpload] Starting ${type} upload...`);
    console.log(`[BannerUpload] File: ${file.name}, Size: ${(file.size / 1024 / 1024).toFixed(2)}MB, Type: ${file.type}`);

    setUploading(type);

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Verify FormData was created correctly
      const formDataFile = formData.get('file');
      if (!formDataFile || !(formDataFile instanceof File)) {
        console.error('[BannerUpload] FormData file is invalid:', formDataFile);
        toast.error('Failed to prepare upload. Please try again.');
        setUploading(null);
        return;
      }
      console.log(`[BannerUpload] FormData prepared, file size in FormData: ${formDataFile.size}`);

      // Get auth headers (don't include Content-Type - let browser set it with boundary)
      const authHeaders = getAuthHeaders ? getAuthHeaders() : {};
      // Remove Content-Type if present - FormData needs browser to set multipart boundary
      delete authHeaders['Content-Type'];
      
      console.log(`[BannerUpload] Making request to ${API}/upload/${type}`);
      
      const response = await fetch(`${API}/upload/${type}`, {
        method: 'POST',
        headers: authHeaders,
        body: formData
      });

      console.log(`[BannerUpload] Response status: ${response.status}`);

      let data;
      try {
        const responseText = await response.text();
        console.log(`[BannerUpload] Response text (first 500 chars): ${responseText.substring(0, 500)}`);
        data = responseText ? JSON.parse(responseText) : {};
      } catch (parseError) {
        console.error('[BannerUpload] Failed to parse response:', parseError);
        data = { detail: 'Server returned invalid response' };
      }

      if (response.ok) {
        toast.success(`${type === 'banner' ? 'Banner' : 'Background'} uploaded successfully!`);
        if (type === 'banner') {
          setBannerPreview(null);
          setBannerFile(null);
        } else {
          setBackgroundPreview(null);
          setBackgroundFile(null);
        }
        fetchPermissions();
        onUploadSuccess?.();
      } else {
        console.error(`[BannerUpload] Upload failed:`, data);
        let errorMsg = 'Upload failed';
        
        if (typeof data.detail === 'object') {
          errorMsg = data.detail.message || data.detail.reason || 'Upload failed';
        } else if (typeof data.detail === 'string') {
          errorMsg = data.detail;
        } else if (response.status === 401) {
          errorMsg = 'Session expired - please log in again';
        } else if (response.status === 403) {
          errorMsg = 'Upgrade required - this feature needs Pro tier or higher';
        } else if (response.status === 413) {
          errorMsg = 'File too large - maximum is 5MB';
        } else if (response.status === 415) {
          errorMsg = 'Invalid format - please use JPG or PNG';
        }
        
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('[BannerUpload] Exception during upload:', error);
      console.error('[BannerUpload] Error name:', error?.name);
      console.error('[BannerUpload] Error message:', error?.message);
      console.error('[BannerUpload] Error stack:', error?.stack?.substring(0, 300));
      
      // More detailed error message for debugging
      let errorMsg = 'Upload failed. Please try again.';
      if (error instanceof TypeError && error.message?.includes('fetch')) {
        errorMsg = 'Network error - please check your connection.';
      } else if (error?.name === 'AbortError') {
        errorMsg = 'Upload timed out. Please try again.';
      } else if (error?.name === 'TypeError' && error.message?.includes('NetworkError')) {
        errorMsg = 'Network error - please check your connection and try again.';
      } else if (error?.message) {
        // Show the actual error message for debugging
        errorMsg = `Upload error: ${error.message}`;
      } else if (error && typeof error === 'object' && Object.keys(error).length === 0) {
        // Empty error object - likely a CORS or network issue
        errorMsg = 'Connection error - please refresh and try again.';
        console.error('[BannerUpload] Empty error object received - likely CORS or network issue');
      }
      toast.error(errorMsg);
    } finally {
      setUploading(null);
    }
  };

  const handleDelete = async (type) => {
    try {
      console.log(`[BannerUpload] Deleting ${type}...`);
      
      // Get auth headers safely
      let headers = {};
      try {
        const authHeaders = getAuthHeaders ? getAuthHeaders() : {};
        headers = authHeaders || {};
        console.log('[BannerUpload] Auth headers present:', !!headers?.Authorization);
      } catch (authError) {
        console.error('[BannerUpload] Failed to get auth headers:', authError);
        // Try localStorage directly
        const token = localStorage.getItem('token');
        if (token) {
          headers = { Authorization: `Bearer ${token}` };
          console.log('[BannerUpload] Using token from localStorage');
        }
      }
      
      if (!headers.Authorization) {
        toast.error('Please log in to remove images');
        return;
      }
      
      const response = await fetch(`${API}/upload/${type}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...headers
        }
      });

      console.log(`[BannerUpload] Delete response status: ${response.status}`);

      if (response.ok) {
        toast.success(`${type === 'banner' ? 'Banner' : 'Background'} removed`);
        fetchPermissions();
        onUploadSuccess?.();
      } else {
        let errorMessage = 'Failed to remove image';
        try {
          const data = await response.json();
          errorMessage = data.detail || errorMessage;
        } catch (e) {
          console.error('[BannerUpload] Could not parse error response');
        }
        console.error(`[BannerUpload] Delete failed: ${errorMessage}`);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('[BannerUpload] Delete error:', error.message);
      toast.error('Failed to remove image. Please try again.');
    }
  };

  const cancelPreview = (type) => {
    if (type === 'banner') {
      setBannerPreview(null);
      setBannerFile(null);
      if (bannerInputRef.current) bannerInputRef.current.value = '';
    } else {
      setBackgroundPreview(null);
      setBackgroundFile(null);
      if (backgroundInputRef.current) backgroundInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      </Card>
    );
  }

  if (!permissions?.has_artist_profile) {
    return null;
  }

  return (
    <div className="space-y-4" data-testid="artist-image-upload">
      {/* Banner Upload Section */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Image className="w-4 h-4 text-primary" />
            <h4 className="font-medium text-sm">Profile Banner</h4>
            <span className="text-xs text-muted-foreground">
              ({permissions?.banner_requirements?.width}x{permissions?.banner_requirements?.height}px)
            </span>
          </div>
          {permissions?.can_upload_banner ? (
            <Badge variant="outline" className="text-xs text-green-500 border-green-500/30">
              Available
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs text-amber-500 border-amber-500/30">
              <Lock className="w-3 h-3 mr-1" /> Pro+
            </Badge>
          )}
        </div>

        {permissions?.can_upload_banner ? (
          <div className="space-y-3">
            {/* Current/Preview Banner */}
            {(bannerPreview || permissions?.banner_url) && (
              <div className="relative rounded-lg overflow-hidden border border-border">
                <img 
                  src={bannerPreview || `${API_BASE_URL}${permissions.banner_url}`}
                  alt="Banner"
                  className="w-full h-24 object-cover"
                />
                {/* Only show Save/Cancel when there's a NEW file selected (both preview AND file exist) */}
                {bannerPreview && bannerFile ? (
                  <div className="absolute inset-0 bg-background/60 flex items-center justify-center gap-2">
                    <Button 
                      size="sm" 
                      onClick={() => handleUpload('banner')}
                      disabled={uploading === 'banner'}
                    >
                      {uploading === 'banner' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
                      Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => cancelPreview('banner')} disabled={uploading === 'banner'}>
                      Cancel
                    </Button>
                  </div>
                ) : permissions?.banner_url && !bannerPreview ? (
                  <Button 
                    size="icon" 
                    variant="destructive"
                    className="absolute top-2 right-2 h-7 w-7"
                    onClick={() => handleDelete('banner')}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                ) : null}
              </div>
            )}

            {/* Show file input when no preview is selected */}
            {!bannerPreview && (
              <>
                <input
                  ref={bannerInputRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0], 'banner')}
                  data-testid="banner-file-input"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  className="w-full"
                  onClick={() => bannerInputRef.current?.click()}
                >
                  <ImagePlus className="w-3 h-3 mr-2" />
                  {permissions?.banner_url ? 'Change Banner' : 'Upload Banner'}
                </Button>
              </>
            )}
          </div>
        ) : (
          <div className="bg-muted/30 rounded-lg p-3 text-center">
            <p className="text-xs text-muted-foreground mb-2">
              Upgrade to Pro tier to upload banners
            </p>
            <Link to="/pricing">
              <Button size="sm" variant="secondary" className="text-xs h-7">
                View Plans <ArrowUpRight className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </div>
        )}
      </Card>

      {/* Background Upload Section */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <h4 className="font-medium text-sm">Profile Background</h4>
            <span className="text-xs text-muted-foreground">
              ({permissions?.background_requirements?.width}x{permissions?.background_requirements?.height}px)
            </span>
          </div>
          {permissions?.can_upload_background ? (
            <Badge variant="outline" className="text-xs text-green-500 border-green-500/30">
              Available
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs text-amber-500 border-amber-500/30">
              <Lock className="w-3 h-3 mr-1" /> Elite+
            </Badge>
          )}
        </div>

        {permissions?.can_upload_background ? (
          <div className="space-y-3">
            {/* Current/Preview Background */}
            {(backgroundPreview || permissions?.background_url) && (
              <div className="relative rounded-lg overflow-hidden border border-border">
                <img 
                  src={backgroundPreview || `${API_BASE_URL}${permissions.background_url}`}
                  alt="Background"
                  className="w-full h-28 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
                
                {/* Only show Save/Cancel when there's a NEW file selected (both preview AND file exist) */}
                {backgroundPreview && backgroundFile ? (
                  <div className="absolute inset-0 bg-background/60 flex items-center justify-center gap-2">
                    <Button 
                      size="sm" 
                      onClick={() => handleUpload('background')}
                      disabled={uploading === 'background'}
                    >
                      {uploading === 'background' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
                      Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => cancelPreview('background')} disabled={uploading === 'background'}>
                      Cancel
                    </Button>
                  </div>
                ) : permissions?.background_url && !backgroundPreview ? (
                  <Button 
                    size="icon" 
                    variant="destructive"
                    className="absolute top-2 right-2 h-7 w-7 z-10"
                    onClick={() => handleDelete('background')}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                ) : null}
              </div>
            )}

            {/* Animated Overlay Note */}
            {permissions?.animated_overlay && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Crown className="w-3 h-3 text-amber-400" />
                Animated overlay {permissions.animated_overlay_enabled ? 'enabled' : 'available'}
              </div>
            )}

            {!backgroundPreview && (
              <>
                <input
                  ref={backgroundInputRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0], 'background')}
                  data-testid="background-file-input"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  className="w-full"
                  onClick={() => backgroundInputRef.current?.click()}
                >
                  <ImagePlus className="w-3 h-3 mr-2" />
                  {permissions?.background_url ? 'Change Background' : 'Upload Background'}
                </Button>
              </>
            )}
          </div>
        ) : (
          <div className="bg-muted/30 rounded-lg p-3 text-center">
            <p className="text-xs text-muted-foreground mb-2">
              Upgrade to Elite tier to upload backgrounds
            </p>
            <Link to="/pricing">
              <Button size="sm" variant="secondary" className="text-xs h-7">
                View Plans <ArrowUpRight className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </div>
        )}
      </Card>

      <p className="text-xs text-muted-foreground">
        Supported formats: JPG, PNG • Images are auto-resized and moderated
      </p>
    </div>
  );
}
